import React, { useEffect, useRef, useState } from "react";
import api, { CLP } from "@/lib/api";
import { Sparkles } from "lucide-react";

/**
 * Drop-in replacement for the description input on repair items.
 * Calls /api/repairs/autocomplete and surfaces previous descriptions
 * (prioritising the current customer's history). Selecting a suggestion
 * autofills the description AND the last known unit_price.
 */
export default function RepairAutocomplete({
  value,
  customerId,
  onChange,
  onPickPrice,
  className,
  placeholder = "Ej: Cambio rodamientos elíptica",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/repairs/autocomplete", {
          params: { q: value || "", customer_id: customerId, limit: 8 },
        });
        setSuggestions(data || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, customerId, open]);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        className={className}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
      />
      {open && (suggestions.length > 0 || loading) && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 bg-white border border-neutral-300 shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testid}-suggestions`}
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-neutral-500 font-mono-kpi uppercase tracking-[0.15em]">
              Buscando…
            </div>
          )}
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(s.description);
                if (onPickPrice && s.last_price) onPickPrice(s.last_price);
                setOpen(false);
              }}
              className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
            >
              {s.from_customer && (
                <Sparkles className="w-3 h-3 text-[#FF5A00] mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{s.description}</div>
                <div className="text-[10px] text-neutral-500 font-mono-kpi">
                  {s.count}× · último: {s.last_date || "—"} · {CLP(s.last_price)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
